import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
] as const;

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

interface MonthPickerProps {
  mode: 'monthly';
  selectedMonth: number; // 1-12
  selectedYear: number;
  onSelect: (month: number, year: number) => void;
}

interface AnnualPickerProps {
  mode: 'annual';
  selectedYear: number;
  onSelect: (year: number) => void;
}

interface WeeklyPickerProps {
  mode: 'weekly';
  selectedWeekStart: Date;
  onSelect: (weekStart: Date) => void;
}

type PeriodPickerProps = MonthPickerProps | AnnualPickerProps | WeeklyPickerProps;

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfMonth(month: number, year: number): number {
  // 0=Sunday, convert to Monday-based (0=Monday)
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function isSameWeek(d1: Date, d2: Date): boolean {
  const w1 = getWeekStart(d1);
  const w2 = getWeekStart(d2);
  return w1.getFullYear() === w2.getFullYear() &&
    w1.getMonth() === w2.getMonth() &&
    w1.getDate() === w2.getDate();
}

// ─── Month Picker (4×3 grid) ────────────────────────────────────────────────

function MonthGrid({ selectedMonth, selectedYear, onSelect, onClose }: {
  selectedMonth: number;
  selectedYear: number;
  onSelect: (month: number, year: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [gridYear, setGridYear] = useState(selectedYear);
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  return (
    <div className="p-3 w-[280px]">
      {/* Year navigation */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setGridYear(y => y - 1)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-sm font-semibold text-gray-900">{gridYear}</span>
        <button
          onClick={() => setGridYear(y => y + 1)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* 4×3 month grid */}
      <div className="grid grid-cols-4 gap-1.5">
        {MONTH_KEYS.map((key, i) => {
          const month = i + 1;
          const isSelected = month === selectedMonth && gridYear === selectedYear;
          const isCurrent = month === currentMonth && gridYear === currentYear;

          return (
            <button
              key={key}
              onClick={() => { onSelect(month, gridYear); onClose(); }}
              className={clsx(
                'px-2 py-2 text-xs font-medium rounded-lg transition-colors text-center',
                isSelected
                  ? 'bg-primary-600 text-white'
                  : isCurrent
                    ? 'bg-primary-100 text-primary-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              {t(`monthsShort.${key}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Year Picker (grid of years) ────────────────────────────────────────────

function YearGrid({ selectedYear, onSelect, onClose }: {
  selectedYear: number;
  onSelect: (year: number) => void;
  onClose: () => void;
}) {
  const [centerYear, setCenterYear] = useState(selectedYear);
  const currentYear = new Date().getFullYear();

  // Show 12 years centered around centerYear
  const startYear = centerYear - 5;
  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  return (
    <div className="p-3 w-[280px]">
      {/* Range navigation */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setCenterYear(y => y - 12)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {startYear} – {startYear + 11}
        </span>
        <button
          onClick={() => setCenterYear(y => y + 12)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* 4×3 year grid */}
      <div className="grid grid-cols-4 gap-1.5">
        {years.map(year => {
          const isSelected = year === selectedYear;
          const isCurrent = year === currentYear;

          return (
            <button
              key={year}
              onClick={() => { onSelect(year); onClose(); }}
              className={clsx(
                'px-2 py-2 text-xs font-medium rounded-lg transition-colors text-center',
                isSelected
                  ? 'bg-primary-600 text-white'
                  : isCurrent
                    ? 'bg-primary-100 text-primary-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              {year}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week Picker (monthly calendar) ─────────────────────────────────────────

function WeekCalendar({ selectedWeekStart, onSelect, onClose }: {
  selectedWeekStart: Date;
  onSelect: (weekStart: Date) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [calMonth, setCalMonth] = useState(selectedWeekStart.getMonth() + 1); // 1-12
  const [calYear, setCalYear] = useState(selectedWeekStart.getFullYear());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const daysInMonth = getDaysInMonth(calMonth, calYear);
  const firstDay = getFirstDayOfMonth(calMonth, calYear);

  // Build calendar cells
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad to fill last row
  while (cells.length % 7 !== 0) cells.push(null);

  const goToPrevMonth = () => {
    if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  const handleDayClick = (day: number) => {
    const date = new Date(calYear, calMonth - 1, day);
    const ws = getWeekStart(date);
    onSelect(ws);
    onClose();
  };

  return (
    <div className="p-3 w-[280px]">
      {/* Month/Year navigation */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={goToPrevMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {t(`months.${MONTH_KEYS[calMonth - 1]}`)} {calYear}
        </span>
        <button
          onClick={goToNextMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_KEYS.map(key => (
          <div key={key} className="text-[10px] font-medium text-gray-400 text-center py-1">
            {t(`days.${key}`)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, i) => {
          if (day === null) return <div key={i} />;

          const date = new Date(calYear, calMonth - 1, day);
          const inSelectedWeek = isSameWeek(date, selectedWeekStart);
          const isToday = date.getTime() === today.getTime();

          // Determine position in week row for rounded corners
          const dayOfWeek = (firstDay + day - 1) % 7; // 0=Mon, 6=Sun
          const isWeekStart = dayOfWeek === 0;
          const isWeekEnd = dayOfWeek === 6;
          const isFirstDay = day === 1;
          const isLastDay = day === daysInMonth;

          return (
            <button
              key={i}
              onClick={() => handleDayClick(day)}
              className={clsx(
                'py-1.5 text-xs text-center transition-colors',
                inSelectedWeek
                  ? 'bg-primary-100 text-primary-800 font-semibold'
                  : 'text-gray-700 hover:bg-gray-100',
                isToday && !inSelectedWeek && 'font-bold text-primary-600',
                isToday && inSelectedWeek && 'font-bold',
                // Rounded corners for the selected week band
                inSelectedWeek && (isWeekStart || isFirstDay) && 'rounded-l-lg',
                inSelectedWeek && (isWeekEnd || isLastDay) && 'rounded-r-lg',
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main PeriodPicker ──────────────────────────────────────────────────────

export default function PeriodPicker(props: PeriodPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Clickable label area - this wraps whatever children call it */}
      <button
        onClick={() => setOpen(o => !o)}
        className="cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
      >
        {props.mode === 'monthly' && <MonthLabel month={props.selectedMonth} year={props.selectedYear} />}
        {props.mode === 'annual' && <YearLabel year={props.selectedYear} />}
        {props.mode === 'weekly' && <WeekLabel weekStart={props.selectedWeekStart} />}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
          {props.mode === 'monthly' && (
            <MonthGrid
              selectedMonth={props.selectedMonth}
              selectedYear={props.selectedYear}
              onSelect={props.onSelect}
              onClose={() => setOpen(false)}
            />
          )}
          {props.mode === 'annual' && (
            <YearGrid
              selectedYear={props.selectedYear}
              onSelect={props.onSelect}
              onClose={() => setOpen(false)}
            />
          )}
          {props.mode === 'weekly' && (
            <WeekCalendar
              selectedWeekStart={props.selectedWeekStart}
              onSelect={props.onSelect}
              onClose={() => setOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Label sub-components ───────────────────────────────────────────────────

function MonthLabel({ month, year }: { month: number; year: number }) {
  const { t } = useTranslation('common');
  return (
    <span className="text-base md:text-lg font-semibold text-gray-900">
      {t(`months.${MONTH_KEYS[month - 1]}`)} {year}
    </span>
  );
}

function YearLabel({ year }: { year: number }) {
  const { t } = useTranslation('common');
  return (
    <span className="text-lg font-semibold text-gray-900">
      {t('period.year', { year })}
    </span>
  );
}

function WeekLabel({ weekStart }: { weekStart: Date }) {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const formatShort = (d: Date) => d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });

  const startLabel = formatShort(weekStart);
  const endLabel = formatShort(weekEnd);
  const year = weekEnd.getFullYear();

  return (
    <span className="text-lg font-semibold text-gray-900">
      {startLabel} – {endLabel} {year}
    </span>
  );
}
